import * as THREE from 'three';

/**
 * Tiny CPU geometry compositor. Every prop and every fort is assembled from
 * primitives here and baked down to one indexed BufferGeometry with vertex
 * colours, so a whole fort is 1-3 draw calls and a prop type is one
 * InstancedMesh.
 */
const _m4 = new THREE.Matrix4();
const _m3 = new THREE.Matrix3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const _c = new THREE.Color();

export class GeoBuilder {
  constructor() { this.pos = []; this.nor = []; this.col = []; this.uv = []; this.idx = []; }

  get count() { return this.pos.length / 3; }

  /**
   * Append `geo` transformed by `mtx`, tinted `color` (hex or THREE.Color).
   *
   * `uvScale` is repeats-per-uv-unit, and it may be a number or `[su, sv]`.
   * Per-axis matters because the primitives here hand every face a 0..1 uv
   * regardless of its world size, so a single scalar forces the same repeat
   * count onto both axes of a face whatever its aspect. A 54 m x 7 m curtain
   * wall is 7.7:1, and at the one scale that made its horizontal blocks the
   * right width its courses came out 8.5 cm tall — eighty of them stacked up
   * a wall, which mips to a flat cream field by twenty-five metres and takes
   * the normal map's relief with it.
   */
  add(geo, mtx, color, uvScale = 1) {
    const su = Array.isArray(uvScale) ? uvScale[0] : uvScale;
    const sv = Array.isArray(uvScale) ? uvScale[1] : uvScale;
    const p = geo.attributes.position, n = geo.attributes.normal, u = geo.attributes.uv;
    const vc = geo.attributes.color;
    const base = this.pos.length / 3;
    _m3.getNormalMatrix(mtx);
    _c.set(color);
    const r = _c.r, g = _c.g, b = _c.b;
    for (let i = 0; i < p.count; i++) {
      _v.set(p.getX(i), p.getY(i), p.getZ(i)).applyMatrix4(mtx);
      this.pos.push(_v.x, _v.y, _v.z);
      if (n) { _v.set(n.getX(i), n.getY(i), n.getZ(i)).applyMatrix3(_m3).normalize(); this.nor.push(_v.x, _v.y, _v.z); }
      else this.nor.push(0, 1, 0);
      if (vc) this.col.push(r * vc.getX(i), g * vc.getY(i), b * vc.getZ(i));
      else this.col.push(r, g, b);
      if (u) this.uv.push(u.getX(i) * su, u.getY(i) * sv); else this.uv.push(0, 0);
    }
    const index = geo.index;
    if (index) for (let i = 0; i < index.count; i++) this.idx.push(base + index.getX(i));
    else for (let i = 0; i < p.count; i++) this.idx.push(base + i);
    return this;
  }

  /** Convenience: place `geo` with position / euler / scale. */
  put(geo, color, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx, uvScale = 1) {
    _e.set(rx, ry, rz);
    _q.setFromEuler(_e);
    _m4.compose(_v.set(x, y, z), _q, new THREE.Vector3(sx, sy, sz));
    return this.add(geo, _m4, color, uvScale);
  }

  /** Raw triangle-soup quad (a, b, c, d in CCW order). */
  quad(a, b, c, d, color, uvs, uvScale = 0) {
    const base = this.pos.length / 3;
    const nx = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(d, a)).normalize();
    _c.set(color);
    const pts = [a, b, c, d];
    for (let i = 0; i < 4; i++) {
      this.pos.push(pts[i].x, pts[i].y, pts[i].z);
      this.nor.push(nx.x, nx.y, nx.z);
      this.col.push(_c.r, _c.g, _c.b);
      if (uvs) this.uv.push(uvs[i * 2], uvs[i * 2 + 1]);
      else if (uvScale > 0) {
        const su = a.distanceTo(b) * uvScale, sv = a.distanceTo(d) * uvScale;
        this.uv.push((i === 1 || i === 2 ? su : 0), (i >= 2 ? sv : 0));
      } else this.uv.push(i === 1 || i === 2 ? 1 : 0, i >= 2 ? 1 : 0);
    }
    this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    return this;
  }

  merge(other, mtx) {
    const g = other.toGeometry();
    this.add(g, mtx || _m4.identity(), 0xffffff);
    g.dispose();
    return this;
  }

  toGeometry(smooth = false) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    geo.setIndex(this.idx);
    if (smooth) geo.computeVertexNormals();
    geo.computeBoundingSphere();
    return geo;
  }
}

/* ------------------------------------------------------------ primitives */
const cache = new Map();
function cached(key, make) { let g = cache.get(key); if (!g) { g = make(); cache.set(key, g); } return g; }

export const BOX = () => cached('box', () => new THREE.BoxGeometry(1, 1, 1));
export const CYL = (seg = 8, rt = 1) => cached('cyl' + seg + '_' + rt, () => new THREE.CylinderGeometry(rt, 1, 1, seg, 1));
/** Open-ended cylinder: half the triangles of CYL, for rings and sleeves whose
 *  end caps are buried inside another solid. */
export const TUBE = (seg = 8, rt = 1) => cached('tube' + seg + '_' + rt, () => new THREE.CylinderGeometry(rt, 1, 1, seg, 1, true));
export const CONE = (seg = 8) => cached('cone' + seg, () => new THREE.ConeGeometry(1, 1, seg, 1));
export const SPH = (w = 8, h = 6) => cached('sph' + w + '_' + h, () => new THREE.SphereGeometry(0.5, w, h));
export const ICO = (d = 0) => cached('ico' + d, () => new THREE.IcosahedronGeometry(0.5, d));
export const PLANE = () => cached('plane', () => new THREE.PlaneGeometry(1, 1));

/**
 * A tapered box (rammed-earth wall segment): bottom footprint bw x bd,
 * top footprint tw x td, height h. Origin at the base centre.
 */
export function taperBox(bw, bd, tw, td, h) {
  const g = new THREE.BufferGeometry();
  const hb = bw / 2, db = bd / 2, ht = tw / 2, dt = td / 2;
  const v = [
    [-hb, 0, -db], [hb, 0, -db], [hb, 0, db], [-hb, 0, db],
    [-ht, h, -dt], [ht, h, -dt], [ht, h, dt], [-ht, h, dt],
  ];
  const faces = [
    [3, 2, 6, 7],   // +z
    [1, 0, 4, 5],   // -z
    [2, 1, 5, 6],   // +x
    [0, 3, 7, 4],   // -x
    [7, 6, 5, 4],   // +y
    [0, 1, 2, 3],   // -y
  ];
  const cx = 0, cy = h / 2, cz = 0;
  const pos = [], nor = [], uv = [], idx = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const n = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3();
  for (const f of faces) {
    a.fromArray(v[f[0]]); b.fromArray(v[f[1]]); c.fromArray(v[f[2]]);
    ab.subVectors(b, a); ac.subVectors(c, a);
    n.crossVectors(ab, ac).normalize();
    // face centre relative to the solid centre decides which way is out
    const fx = (v[f[0]][0] + v[f[1]][0] + v[f[2]][0] + v[f[3]][0]) / 4 - cx;
    const fy = (v[f[0]][1] + v[f[1]][1] + v[f[2]][1] + v[f[3]][1]) / 4 - cy;
    const fz = (v[f[0]][2] + v[f[1]][2] + v[f[2]][2] + v[f[3]][2]) / 4 - cz;
    const flip = (n.x * fx + n.y * fy + n.z * fz) < 0;
    if (flip) n.negate();
    const order = flip ? [3, 2, 1, 0] : [0, 1, 2, 3];
    const base = pos.length / 3;
    for (let i = 0; i < 4; i++) {
      const p = v[f[order[i]]];
      pos.push(p[0], p[1], p[2]);
      nor.push(n.x, n.y, n.z);
      uv.push(i === 1 || i === 2 ? 1 : 0, i >= 2 ? 1 : 0);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

/**
 * Build a closed convex solid from a vertex list and quad/tri faces. Winding
 * and normals are derived from the centroid, so face order can never invert.
 * `faces` entries are [i0,i1,i2,i3] (repeat the last index for a triangle).
 */
export function convexSolid(v, faces, uvScale = 1) {
  let cx = 0, cy = 0, cz = 0;
  for (const p of v) { cx += p[0]; cy += p[1]; cz += p[2]; }
  cx /= v.length; cy /= v.length; cz /= v.length;
  const pos = [], nor = [], uv = [], idx = [];
  const n = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3();
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (const f of faces) {
    a.fromArray(v[f[0]]); b.fromArray(v[f[1]]); c.fromArray(v[f[2]]);
    if (a.distanceTo(b) < 1e-6 || b.distanceTo(c) < 1e-6) continue;
    ab.subVectors(b, a); ac.subVectors(c, a);
    n.crossVectors(ab, ac);
    if (n.lengthSq() < 1e-12) continue;
    n.normalize();
    let fx = 0, fy = 0, fz = 0;
    for (const i of f) { fx += v[i][0]; fy += v[i][1]; fz += v[i][2]; }
    fx = fx / f.length - cx; fy = fy / f.length - cy; fz = fz / f.length - cz;
    const flip = (n.x * fx + n.y * fy + n.z * fz) < 0;
    if (flip) n.negate();
    const order = flip ? [3, 2, 1, 0] : [0, 1, 2, 3];
    const base = pos.length / 3;
    // planar uv from the two longest in-plane axes
    const ux = ab.clone().normalize();
    const vy = new THREE.Vector3().crossVectors(n, ux);
    for (let i = 0; i < 4; i++) {
      const p = v[f[order[i]]];
      pos.push(p[0], p[1], p[2]);
      nor.push(n.x, n.y, n.z);
      const dx = p[0] - a.x, dy = p[1] - a.y, dz = p[2] - a.z;
      uv.push((dx * ux.x + dy * ux.y + dz * ux.z) * uvScale, (dx * vy.x + dy * vy.y + dz * vy.z) * uvScale);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

/**
 * Chinese hip roof with flying eaves.
 *   w, d      plan of the building (ridge runs along X)
 *   over      eave overhang beyond the plan
 *   rise      height from eave line to ridge
 *   flick     how far the eave corners curl upward
 * Origin at the eave-line centre; roof surface only (add a ridge beam yourself).
 */
export function chineseRoof(w, d, over, rise, flick = 0.55, seg = 5) {
  const hw = w / 2 + over, hd = d / 2 + over;
  const ridgeHalf = Math.max(0.01, (w - d) / 2);
  const pos = [], nor = [], uv = [], idx = [];

  // surface height above the eave line for a normalised inset s (0 eave, 1 ridge)
  const prof = (s) => rise * Math.pow(s, 1.75);

  function panel(mapFn, uSeg, vSeg) {
    const base = pos.length / 3;
    for (let j = 0; j <= vSeg; j++) {
      const s = j / vSeg;
      for (let i = 0; i <= uSeg; i++) {
        const t = i / uSeg;
        const p = mapFn(t, s);
        pos.push(p[0], p[1], p[2]);
        nor.push(0, 1, 0);
        uv.push(t * (w * 0.42), s * (d * 0.42));
      }
    }
    // decide the winding from the first quad so every panel faces up
    const i0 = base * 3, i1 = (base + 1) * 3, i2 = (base + uSeg + 1) * 3;
    const ux = pos[i1] - pos[i0], uy = pos[i1 + 1] - pos[i0 + 1], uz = pos[i1 + 2] - pos[i0 + 2];
    const vx = pos[i2] - pos[i0], vy = pos[i2 + 1] - pos[i0 + 1], vz = pos[i2 + 2] - pos[i0 + 2];
    const ny = uz * vx - ux * vz;
    const cw = ny < 0;
    for (let j = 0; j < vSeg; j++) for (let i = 0; i < uSeg; i++) {
      const a = base + j * (uSeg + 1) + i, b = a + 1, c = a + uSeg + 1, dd = c + 1;
      if (cw) idx.push(a, c, b, b, c, dd);
      else idx.push(a, b, c, b, dd, c);
    }
  }

  // corner curl: strongest at the panel ends
  const curl = (t) => Math.pow(Math.abs(t * 2 - 1), 3.2);

  // front (+z) and back (-z) trapezoids
  for (const sgn of [1, -1]) {
    panel((t, s) => {
      const x0 = -hw + t * (hw * 2);
      const x1 = -ridgeHalf + t * (ridgeHalf * 2);
      const x = x0 + (x1 - x0) * s;
      const z = sgn * hd * (1 - s);
      const y = prof(s) + flick * curl(t) * Math.pow(1 - s, 2.4);
      return [x, y, z];
    }, 8, seg);
  }
  // left (-x) and right (+x) triangles
  for (const sgn of [1, -1]) {
    panel((t, s) => {
      const z0 = -hd + t * (hd * 2);
      const z = z0 * (1 - s);
      const x = sgn * (hw + (ridgeHalf - hw) * s);
      const y = prof(s) + flick * curl(t) * Math.pow(1 - s, 2.4);
      return [x, y, z];
    }, 6, seg);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Irregular boulder: an icosahedron pushed around by a hash. */
export function rockGeo(detail = 1, seed = 5, lumpy = 0.34) {
  const g = new THREE.IcosahedronGeometry(0.5, detail);
  const p = g.attributes.position;
  const h = (x, y, z) => {
    let n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + seed) * 43758.5453;
    return n - Math.floor(n);
  };
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const k = 1 + (h(x * 3, y * 3, z * 3) - 0.5) * lumpy * 2;
    p.setXYZ(i, x * k, y * k * 0.82, z * k);
  }
  g.computeVertexNormals();
  return g;
}

/**
 * Ridge tent: a closed canvas prism with a ridge pole, eave trim, guy pegs and
 * a dark doorway. Origin at the ground centre, ridge along X.
 */
export function tentGeo(L, W, H, col, colDark, colTrim) {
  const b = new GeoBuilder();
  const hl = L / 2, hw = W / 2;
  const v = [
    [-hl, 0.06, -hw], [hl, 0.06, -hw], [hl, 0.06, hw], [-hl, 0.06, hw],
    [-hl, H, 0], [hl, H, 0],
  ];
  const faces = [
    [3, 2, 5, 4],   // +z slope
    [1, 0, 4, 5],   // -z slope
    [2, 1, 5, 5],   // +x gable
    [0, 3, 4, 4],   // -x gable
    [0, 1, 2, 3],   // floor
  ];
  b.put(convexSolid(v, faces, 0.5), col, 0, 0, 0, 0, 0, 0, 1);
  // ridge pole overhanging both ends
  b.put(CYL(5, 1), colTrim, 0, H + 0.06, 0, 0, 0, 1.5708, 0.055, L * 1.12, 0.055, 1);
  // eave trim bands
  for (const sg of [1, -1]) b.put(BOX(), colDark, 0, 0.16, sg * hw, 0, 0, 0, L * 1.02, 0.12, 0.09, 1);
  // ridge seam
  b.put(BOX(), colDark, 0, H - 0.03, 0, 0, 0, 0, L * 1.0, 0.06, 0.18, 1);
  // doorway
  b.put(BOX(), 0x2b2419, -hl - 0.03, H * 0.30, 0, 0, 0, 0, 0.06, H * 0.58, W * 0.42, 1);
  // corner pegs + guy lines
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    b.put(CYL(4, 1), colTrim, sx * hl * 0.92, 0.2, sz * (hw + 0.55), sx * 0.45, 0, sz * 0.35, 0.05, 0.7, 0.05, 1);
    b.put(BOX(), colTrim, sx * (hl * 0.5), H * 0.62, sz * (hw * 0.75), sz * 0.9, 0, 0, 0.03, W * 1.05, 0.03, 1);
  }
  return b.toGeometry();
}
